// @flow

import type { ProductId, Process, ProductType } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import { Base, Text } from '@gandi/ui';

import { DomainConfigurationCellContents } from './DomainConfiguration/CellContents';
import { DomainContactsCellContents } from './DomainContacts/CellContents';
import { OwnerCellContents } from './Owner/CellContents';

import styles from './ProductSetup.scss';

type Props = {
  firstProductId: ProductId,
  productType: ProductType,
  process: Process,
};

function useProductSetup(
  productType: ProductType,
  process: Process,
): {
  collapsible: boolean,
  domainChangeOwner: boolean,
  domainCreate: boolean,
  domainTransfer: boolean,
} {
  const domain = productType === 'domain';
  const domainCreate = domain && process === 'create';
  const domainTransfer = domain && process === 'transfer';
  const domainChangeOwner = domain && process === 'change_owner';

  const collapsible = domainCreate;

  return {
    collapsible,
    domainChangeOwner,
    domainCreate,
    domainTransfer,
  };
}

function ProductSetup({
  firstProductId,
  productType,
  process,
}: Props): null | React.Node {
  const { domainChangeOwner, domainCreate, domainTransfer } = useProductSetup(
    productType,
    process,
  );

  if (domainChangeOwner) {
    return null;
  }

  return (
    <ProductSetupContainer>
      {!domainTransfer && <OwnerCellContents productId={firstProductId} />}
      {(domainCreate || domainTransfer) && (
        <>
          <DomainConfigurationCellContents process={process} />
          <DomainContactsCellContents productId={firstProductId} />
        </>
      )}
    </ProductSetupContainer>
  );
}

function ProductSetupContainer({
  children,
}: {
  children: React.Node,
}): React.MixedElement {
  const { __ } = useTranslator();

  return (
    <Base className={styles.root} color="weak" flex flexCol gap="spacing-2x">
      <div className={styles.container}>
        <Text weight="semibold">{__('Setup details')}</Text>
        {children}
      </div>
    </Base>
  );
}

export default ProductSetup;
